import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useSearchParamState } from "use-search-param-state";
import { useRegions } from "../../admin/dictionaries/useRegions";
import { useSearchParamArray } from "./useSearchParamArray";
import { useTagParams } from "./useTagParams";

interface ActiveFiltersProps {
  ecommerceOnly: boolean;
  toggleEcommerceOnly: () => void;
}

const ActiveFilters = ({ ecommerceOnly, toggleEcommerceOnly }: ActiveFiltersProps) => {
  const { tags, setTags } = useTagParams();
  const [searchQuery, setSearchQuery] = useSearchParamState("name", "");
  const [, setSearchParams] = useSearchParams();
  const { regions } = useRegions();
  const [activeRegions, setRegions] = useSearchParamArray("region");

  if (tags.length === 0 && !ecommerceOnly && !searchQuery && activeRegions.length === 0) {
    return null;
  }

  const removeTag = (category: string, value: string) => {
    setTags(tags.filter((tag) => !(tag.category === category && tag.value === value)));
  };

  const clearAll = () => {
    setSearchParams({});

    if (ecommerceOnly) {
      toggleEcommerceOnly();
    }
  };
  return (
    <Box
      sx={{
        mb: 2,
        px: 2,
        py: 1.25,
        borderRadius: 1,
        bgcolor: "rgba(0,0,0,0.04)",
        border: "1px solid rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
      }}
    >
      <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: "nowrap", flexShrink: 0, fontWeight: 600 }}>
        Aktywne filtry:
      </Typography>

      <Stack direction="row" spacing={0.75} useFlexGap sx={{ flex: 1, minWidth: 0, flexWrap: "wrap" }}>
        {searchQuery && (
          <Chip
            label={`Szukaj: „${searchQuery}”`}
            size="small"
            onDelete={() => setSearchQuery("")}
            sx={{
              bgcolor: "rgba(0,0,0,0.06)",
              color: "textSecondary",
              fontWeight: 500,
              "& .MuiChip-deleteIcon": { color: "text.disabled", "&:hover": { color: "text.secondary" } },
            }}
          />
        )}

        {ecommerceOnly && (
          <Chip
            label="Dostępne w e-commerce"
            size="small"
            onDelete={toggleEcommerceOnly}
            sx={{
              bgcolor: "rgba(0,0,0,0.06)",
              color: "textSecondary",
              fontWeight: 500,
              "& .MuiChip-deleteIcon": { color: "text.disabled", "&:hover": { color: "text.secondary" } },
            }}
          />
        )}
        {activeRegions.map((region) => (
          <Chip
            key={region}
            label={`Region: ${regions.find((r) => r.id.id === region)?.name}`}
            size="small"
            onDelete={() => setRegions(activeRegions.filter((r) => r !== region))}
            sx={{
              bgcolor: "rgba(0,0,0,0.06)",
              color: "textSecondary",
              fontWeight: 500,
              "& .MuiChip-deleteIcon": { color: "text.disabled", "&:hover": { color: "text.secondary" } },
            }}
          />
        ))}
        {tags.map((tag) => (
          <Chip
            key={`${tag.category}-${tag.value}`}
            label={`${tag.category}: ${tag.value}`}
            size="small"
            onDelete={() => removeTag(tag.category, tag.value)}
            sx={{
              bgcolor: "rgba(0,0,0,0.06)",
              color: "text.secondary",
              fontWeight: 500,
              "& .MuiChip-deleteIcon": { color: "text.disabled", "&:hover": { color: "text.secondary" } },
            }}
          />
        ))}
      </Stack>
      <Button
        size="small"
        color="inherit"
        onClick={clearAll}
        startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
        sx={{
          textTransform: "none",
          color: "text.secondary",
          fontWeight: 600,
          flexShrink: 0,
          "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
        }}
      >
        Wyczyść
      </Button>
    </Box>
  );
};

export default ActiveFilters;
